import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useUser } from '@/hooks/UserContext';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { LocalStorageKeys } from '@/constants/localStorage';

export interface Notification {
    id: number;
    title: string;
    body: string;
    publishStartAt: string;
    publishEndAt: string | null;
    read: boolean;
    createdAt: string;
    displayType: string;
}

interface NotificationSidebarContextType {
    isOpen: boolean;
    openSidebar: () => void;
    closeSidebar: () => void;
    hasUnread: boolean;
    setHasUnread: (hasUnread: boolean) => void;
    notifications: Notification[];
    setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
    isLoadingNotifications: boolean;
    setIsLoadingNotifications: (loading: boolean) => void;
}

const NotificationSidebarContext = createContext<NotificationSidebarContextType | undefined>(
    undefined,
);

export const NotificationSidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { userInfo } = useUser();
    const [isOpen, setIsOpen] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    // お知らせAPIのキャッシュ（SPAなので、既にデータがあれば再取得しない）
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

    // 初期表示時にローカルストレージから開閉状態を復元
    useEffect(() => {
        const restoreState = async () => {
            if (!userInfo?.userId) {
                return;
            }

            try {
                const saved = await getLocalStorage(userInfo.userId, LocalStorageKeys.NOTIFICATION_SIDEBAR_OPEN);
                // 保存された状態に合わせて開閉状態を設定
                if (saved === 'true') {
                    setIsOpen(true);
                } else {
                    setIsOpen(false);
                }
            } catch (error) {
                console.error('Error restoring notification sidebar state:', error);
                // エラー時は閉じた状態にする
                setIsOpen(false);
            } finally {
                setIsInitialized(true);
            }
        };

        restoreState();
    }, [userInfo?.userId]);

    // 開閉状態をローカルストレージに保存
    const saveState = async (open: boolean) => {
        if (!userInfo?.userId || !isInitialized) {
            return;
        }

        try {
            await setLocalStorage(userInfo.userId, LocalStorageKeys.NOTIFICATION_SIDEBAR_OPEN, String(open));
        } catch (error) {
            console.error('Error saving notification sidebar state:', error);
        }
    };

    const openSidebar = () => {
        setIsOpen(true);
        // 初期化完了後のみ保存
        if (isInitialized) {
            saveState(true);
        }
    };

    const closeSidebar = () => {
        setIsOpen(false);
        // 初期化完了後のみ保存
        if (isInitialized) {
            saveState(false);
        }
    };

    return (
        <NotificationSidebarContext.Provider
            value={{
                isOpen,
                openSidebar,
                closeSidebar,
                hasUnread,
                setHasUnread,
                notifications,
                setNotifications,
                isLoadingNotifications,
                setIsLoadingNotifications,
            }}
        >
            {children}
        </NotificationSidebarContext.Provider>
    );
};

export const useNotificationSidebar = () => {
    const context = useContext(NotificationSidebarContext);
    if (context === undefined) {
        throw new Error('useNotificationSidebar must be used within a NotificationSidebarProvider');
    }
    return context;
};

